import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import { ReactNode, useMemo } from 'react';

import { EntityType } from '@/core/graphql/generated/types';
import {
	useDcrFormFilteredFields,
	useDcrFormSearchTerm
} from '@/core/store/dcrs/dcrSelectors';
import { HcoSection } from '@/pages/MyDCRsPage/ViewDCRModal/_hooks/useHcoFieldNames';
import { HcpSection } from '@/pages/MyDCRsPage/ViewDCRModal/_hooks/useHcpFieldNames';
import { mergeSx } from '@/utils/mergeSx';

import { useDcrContext } from '../_context/DcrContext';

type Props = {
	name: string;
	section: HcoSection | HcpSection;
	children: ReactNode;
	enabled?: boolean;
	customSx?: SxProps<Theme>;
};

const hcpSimpleViewFields = [
	'newEntityCountryId',
	'firstNameCda_V',
	'lastNameCda_V',
	'hcpType',
	'notes',
	'isThirdPartyDataConfirmed',

	// Local specialties
	'allSpecLocal_VId',
	'id',
	'isPrimary',

	// Affiliations
	'hcoNameCda_V',
	'typeOfRelationship_VId',
	'jobTitle_V',

	// Addresses
	'streetAddress1Cda_V',
	'streetAddress2Cda_V',
	'cityCda_V',
	'stateCda_V',
	'postalCodeCda_V',
	'countryCda_V'
];

const hcoSimpleViewFields = [
	'newEntityCountryId',
	'hcoNameCda_V',
	'hcoType',
	'notes',
	'isThirdPartyDataConfirmed',

	// Affiliations
	'hcoNameCda_V',
	'typeOfRelationship_VId',

	// Addresses
	'streetAddress1Cda_V',
	'streetAddress2Cda_V',
	'cityCda_V',
	'stateCda_V',
	'postalCodeCda_V',
	'countryCda_V'
];

export const FilteredField = ({
	name,
	section,
	children,
	enabled = true,
	customSx
}: Props) => {
	const transformedName = name.split('.').pop() ?? name;
	const query = useDcrFormSearchTerm();
	const fields = useDcrFormFilteredFields(section);
	const { isSimpleView, entityType } = useDcrContext();

	const isVisible = useMemo(
		() =>
			isSimpleView
				? !!(
						entityType === EntityType.Hcp
							? hcpSimpleViewFields
							: hcoSimpleViewFields
					).includes(transformedName)
				: !enabled || query === '' || !!fields.includes(transformedName),
		[enabled, isSimpleView, query, fields, entityType, transformedName]
	);

	return (
		<Box sx={mergeSx({ display: isVisible ? 'block' : 'none' }, customSx)}>
			{children}
		</Box>
	);
};
